import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Mic, Square, Play, Pause, Trash2, Check, X, Clock, FileText, AlertCircle, Plus, Pencil, Copy, MessageCircle, Eye, EyeOff, CheckCircle2, Loader2, ChevronDown, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// 10 minutes in milliseconds
const CHUNK_INTERVAL_MS = 10 * 60 * 1000;

const CustomerLessonNotesPage = () => {
  const { customerId, horseId } = useParams();
  const [searchParams] = useSearchParams();
  const autostart = searchParams.get('autostart') === 'true';
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [customer, setCustomer] = useState(null);
  const [horse, setHorse] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAutoStarted, setHasAutoStarted] = useState(false);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingWarning, setRecordingWarning] = useState(null);
  
  // Chunking state
  const [savedChunks, setSavedChunks] = useState([]);  // Array of { chunkNumber, status: 'saving'|'saved'|'error' }
  const [currentChunkNumber, setCurrentChunkNumber] = useState(0);
  const [isProcessingChunk, setIsProcessingChunk] = useState(false);
  
  // Processing state
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [currentNote, setCurrentNote] = useState(null);
  const [showSummaryReview, setShowSummaryReview] = useState(false);
  const [editedSummary, setEditedSummary] = useState({ worked_on: [], homework: [] });
  
  // New point input state
  const [newWorkedOnItem, setNewWorkedOnItem] = useState('');
  const [newHomeworkItem, setNewHomeworkItem] = useState('');
  
  // Edit existing note state
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteData, setEditingNoteData] = useState({ worked_on: [], homework: [] });
  const [newEditWorkedOn, setNewEditWorkedOn] = useState('');
  const [newEditHomework, setNewEditHomework] = useState('');
  
  // Expanded notes state (for collapsible previous notes)
  const [expandedNotes, setExpandedNotes] = useState({});
  
  // Language selection
  const [selectedLanguage, setSelectedLanguage] = useState('da');
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const allAudioChunksRef = useRef([]);  // Keep ALL audio for final processing
  const timerRef = useRef(null);
  const chunkTimerRef = useRef(null);  // Timer for 10-minute chunks
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const chunkStartTimeRef = useRef(0);  // Track when current chunk started

  useEffect(() => {
    fetchCustomerAndNotes();
  }, [customerId, horseId]);

  // Process a chunk in background (transcribe + summarize)
  const processChunkInBackground = async (chunkBlob, chunkNumber, noteId) => {
    // Update UI to show we're saving this chunk
    setSavedChunks(prev => [...prev, { chunkNumber, status: 'saving' }]);
    
    try {
      // Upload and transcribe the chunk
      const formData = new FormData();
      formData.append('audio_file', chunkBlob, `chunk_${chunkNumber}.webm`);
      
      const transcribeRes = await axios.post(
        `${API}/lesson-notes/${noteId}/transcribe-chunk?chunk_number=${chunkNumber}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 180000
        }
      );
      
      // Now summarize this chunk
      await axios.post(
        `${API}/lesson-notes/${noteId}/summarize-chunk?chunk_number=${chunkNumber}&transcript=${encodeURIComponent(transcribeRes.data.transcript)}`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        }
      );
      
      // Update UI to show chunk saved
      setSavedChunks(prev => prev.map(c => 
        c.chunkNumber === chunkNumber ? { ...c, status: 'saved' } : c
      ));
      
    } catch (error) {
      console.error(`Error processing chunk ${chunkNumber}:`, error);
      setSavedChunks(prev => prev.map(c => 
        c.chunkNumber === chunkNumber ? { ...c, status: 'error' } : c
      ));
    }
  };

  // Harvest current audio and restart recording seamlessly
  const harvestChunk = useCallback(async () => {
    if (!mediaRecorderRef.current || !currentNote || isPaused) return;
    
    const recorder = mediaRecorderRef.current;
    const chunkNum = currentChunkNumber;
    
    // Request the current data
    recorder.requestData();
    
    // Small delay to ensure data is collected
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Create blob from collected chunks
    if (audioChunksRef.current.length > 0) {
      const mimeType = recorder.mimeType || 'audio/webm';
      const chunkBlob = new Blob(audioChunksRef.current, { type: mimeType });
      
      // Also add to the full recording for final playback
      allAudioChunksRef.current.push(...audioChunksRef.current);
      
      // Reset chunks for next segment
      audioChunksRef.current = [];
      
      // Process this chunk in background
      processChunkInBackground(chunkBlob, chunkNum, currentNote.id);
      
      // Increment chunk counter
      setCurrentChunkNumber(prev => prev + 1);
    }
  }, [currentNote, currentChunkNumber, isPaused, token]);

  // Set up the 10-minute chunk interval
  useEffect(() => {
    if (isRecording && !isPaused && currentNote) {
      // Start chunk timer
      chunkTimerRef.current = setInterval(() => {
        harvestChunk();
      }, CHUNK_INTERVAL_MS);
      
      return () => {
        if (chunkTimerRef.current) {
          clearInterval(chunkTimerRef.current);
        }
      };
    }
  }, [isRecording, isPaused, currentNote, harvestChunk]);

  // Handle visibility change (phone calls, app switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isRecording) {
        // Page is hidden but recording continues
        setRecordingWarning('Optagelse fortsætter i baggrunden...');
        // Make sure timer continues
      } else if (!document.hidden && isRecording) {
        setRecordingWarning(null);
      }
    };

    const handleBeforeUnload = (e) => {
      if (isRecording || audioBlob) {
        e.preventDefault();
        e.returnValue = 'Du har en igangværende optagelse. Er du sikker på, at du vil forlade siden?';
        return e.returnValue;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isRecording, audioBlob]);

  const fetchCustomerAndNotes = async () => {
    try {
      // First try note_customers, then fall back to jcp_customers
      let foundCustomer = null;
      try {
        const noteCustomersRes = await axios.get(`${API}/note-customers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        foundCustomer = noteCustomersRes.data.find(c => c.id === customerId);
      } catch (e) {
        // Ignore - try jcp_customers next
      }
      
      if (!foundCustomer) {
        const jcpCustomersRes = await axios.get(`${API}/admin/jcp-customers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        foundCustomer = jcpCustomersRes.data.find(c => c.id === customerId);
      }
      setCustomer(foundCustomer);
      
      // Set the customer's default language
      if (foundCustomer?.language) {
        setSelectedLanguage(foundCustomer.language);
      }
      
      // Fetch horse info from note_horses (NOT jcp_horses)
      if (horseId) {
        const horsesRes = await axios.get(`${API}/note-horses/customer/${customerId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const foundHorse = horsesRes.data.find(h => h.id === horseId);
        setHorse(foundHorse);
      }
      
      // Fetch notes - filter by horse if horseId is provided
      const notesRes = await axios.get(`${API}/lesson-notes/customer/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter notes by horse if horseId is provided
      const filteredNotes = horseId 
        ? notesRes.data.filter(n => n.horse_id === horseId)
        : notesRes.data;
      setNotes(filteredNotes);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  // Auto-start recording if autostart param is true
  useEffect(() => {
    if (!loading && autostart && !hasAutoStarted && horse && customer) {
      setHasAutoStarted(true);
      // Small delay to ensure page is fully rendered
      setTimeout(() => {
        startRecording();
      }, 500);
    }
  }, [loading, autostart, hasAutoStarted, horse, customer]);

  const startRecording = async () => {
    try {
      // Request audio with settings optimized for speech
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      streamRef.current = stream;
      
      // Create new note first - include horse_id if available
      const noteRes = await axios.post(`${API}/lesson-notes`, {
        customer_id: customerId,
        customer_name: customer ? `${customer.first_name} ${customer.last_name}` : 'Unknown',
        horse_id: horseId || null,
        horse_name: horse ? horse.name : null,
        lesson_date: new Date().toISOString(),
        language: selectedLanguage
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setCurrentNote(noteRes.data);
      
      // Try different mime types for compatibility
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = '';
          }
        }
      }
      
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      allAudioChunksRef.current = [];  // Reset all audio chunks
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        // Combine ALL chunks (including from chunking) into final blob
        const mimeTypeForBlob = mediaRecorder.mimeType || 'audio/webm';
        const allChunks = allAudioChunksRef.current.length > 0 
          ? allAudioChunksRef.current 
          : audioChunksRef.current;
        const blob = new Blob(allChunks, { type: mimeTypeForBlob });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
      };
      
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        toast.error('Optagelsesfejl. Prøv igen.');
      };
      
      // Request data every 5 seconds to ensure we don't lose data
      mediaRecorder.start(5000);
      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);
      setSavedChunks([]);  // Reset chunk status
      setCurrentChunkNumber(0);  // Reset chunk counter
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success('Optagelse startet');
    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Mikrofon ikke tilladt. Tjek browserindstillinger.');
      } else if (error.name === 'NotFoundError') {
        toast.error('Ingen mikrofon fundet.');
      } else {
        toast.error('Kunne ikke starte mikrofon. Tjek browserrettigheder.');
      }
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      clearInterval(timerRef.current);
      setIsPaused(true);
      toast.info('Optagelse pauset');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      setIsPaused(false);
      toast.success('Optagelse genoptaget');
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      // Clear chunk timer
      if (chunkTimerRef.current) {
        clearInterval(chunkTimerRef.current);
      }
      
      // Request final data before stopping
      mediaRecorderRef.current.requestData();
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Combine current chunks with all previous chunks for final audio
      if (audioChunksRef.current.length > 0) {
        allAudioChunksRef.current.push(...audioChunksRef.current);
        
        // If there were any chunks being recorded, process the final partial chunk
        if (currentChunkNumber > 0 || recordingTime > 0) {
          const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
          const finalChunkBlob = new Blob(audioChunksRef.current, { type: mimeType });
          // Process final chunk if it's substantial (> 30 seconds)
          if (recordingTime % 600 > 30) {
            processChunkInBackground(finalChunkBlob, currentChunkNumber, currentNote?.id);
          }
        }
      }
      
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      clearInterval(timerRef.current);
      setRecordingWarning(null);
      toast.success('Optagelse stoppet');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const transcribeAndSummarize = async () => {
    if (!audioBlob || !currentNote) return;
    
    // Check if we already have chunk summaries (from background processing)
    const hasChunks = savedChunks.length > 0 && savedChunks.some(c => c.status === 'saved');
    
    if (hasChunks) {
      // We have chunk summaries - combine them
      setIsSummarizing(true);
      try {
        const combineRes = await axios.post(
          `${API}/lesson-notes/${currentNote.id}/combine-summaries`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
        );
        
        setEditedSummary(combineRes.data);
        setShowSummaryReview(true);
        toast.success('Sammendrag genereret fra chunks');
      } catch (error) {
        console.error('Error combining summaries:', error);
        // Fallback to regular processing
        await processFullAudio();
      } finally {
        setIsSummarizing(false);
      }
    } else {
      // No chunks - process full audio (short recordings)
      await processFullAudio();
    }
  };
  
  const processFullAudio = async () => {
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.webm');
      
      const transcribeRes = await axios.post(
        `${API}/lesson-notes/${currentNote.id}/transcribe`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 180000 // 3 minute timeout for long recordings
        }
      );
      
      toast.success('Transskription færdig');
      setIsTranscribing(false);
      setIsSummarizing(true);
      
      const summaryRes = await axios.post(
        `${API}/lesson-notes/${currentNote.id}/summarize`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      
      setEditedSummary(summaryRes.data);
      setShowSummaryReview(true);
      toast.success('Sammendrag genereret');
      
    } catch (error) {
      console.error('Error processing:', error);
      toast.error('Kunne ikke behandle optagelsen');
    } finally {
      setIsTranscribing(false);
      setIsSummarizing(false);
    }
  };

  const saveSummary = async (copyToClipboard = false) => {
    if (!currentNote) return;
    
    try {
      await axios.put(`${API}/lesson-notes/${currentNote.id}`, {
        worked_on: editedSummary.worked_on,
        homework: editedSummary.homework
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Delete transcript (keep only summary)
      await axios.delete(`${API}/lesson-notes/${currentNote.id}/transcript`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Copy to clipboard if requested
      if (copyToClipboard) {
        const customerName = customer ? `${customer.first_name} ${customer.last_name}` : 'Kunde';
        const date = new Date().toLocaleDateString('da-DK', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
        
        let text = `📝 Undervisningsnoter for ${customerName}\n`;
        text += `📅 ${date}\n\n`;
        
        if (editedSummary.worked_on && editedSummary.worked_on.length > 0) {
          text += `✅ Vi arbejdede med:\n`;
          editedSummary.worked_on.forEach(item => {
            text += `• ${item}\n`;
          });
          text += `\n`;
        }
        
        if (editedSummary.homework && editedSummary.homework.length > 0) {
          text += `🎯 Hjemmearbejde:\n`;
          editedSummary.homework.forEach(item => {
            text += `• ${item}\n`;
          });
        }
        
        text += `\n— JCP Teaching`;
        
        try {
          await navigator.clipboard.writeText(text);
          toast.success('Gemt og kopieret til udklipsholder! Indsæt i WhatsApp');
        } catch (clipboardError) {
          console.error('Clipboard error:', clipboardError);
          toast.success('Noter gemt (kunne ikke kopiere til udklipsholder)');
        }
      } else {
        toast.success('Noter gemt');
      }
      
      // Reset state
      setShowSummaryReview(false);
      setCurrentNote(null);
      setAudioBlob(null);
      setAudioUrl(null);
      setRecordingTime(0);
      
      fetchCustomerAndNotes();
      
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Kunne ikke gemme');
    }
  };

  // Copy note to clipboard for WhatsApp
  const copyNoteToClipboard = async (note) => {
    const customerName = customer ? `${customer.first_name} ${customer.last_name}` : 'Kunde';
    const horseName = note.horse_name || horse?.name || null;
    const date = new Date(note.lesson_date).toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    
    let text = `📝 Undervisningsnoter`;
    if (horseName) {
      text += ` - ${horseName}`;
    }
    text += `\n👤 ${customerName}\n`;
    text += `📅 ${date}\n\n`;
    
    if (note.worked_on && note.worked_on.length > 0) {
      text += `✅ Vi arbejdede med:\n`;
      note.worked_on.forEach(item => {
        text += `• ${item}\n`;
      });
      text += `\n`;
    }
    
    if (note.homework && note.homework.length > 0) {
      text += `🎯 Hjemmearbejde:\n`;
      note.homework.forEach(item => {
        text += `• ${item}\n`;
      });
    }
    
    text += `\n— JCP Teaching`;
    
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Kopieret til udklipsholder! Indsæt i WhatsApp');
    } catch (error) {
      console.error('Copy failed:', error);
      toast.error('Kunne ikke kopiere');
    }
  };

  // Toggle release to portal
  const toggleReleaseToPortal = async (note) => {
    try {
      const endpoint = note.released_to_portal 
        ? `${API}/lesson-notes/${note.id}/unrelease`
        : `${API}/lesson-notes/${note.id}/release`;
      
      await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(note.released_to_portal 
        ? 'Noten er nu skjult fra kundens profil'
        : 'Noten er nu frigivet til kundens profil'
      );
      fetchCustomerAndNotes();
    } catch (error) {
      console.error('Error toggling release:', error);
      toast.error('Kunne ikke ændre frigivelse');
    }
  };

  const cancelRecording = async () => {
    // Stop recording if active
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    clearInterval(timerRef.current);
    if (chunkTimerRef.current) {
      clearInterval(chunkTimerRef.current);
    }
    
    if (currentNote) {
      try {
        await axios.delete(`${API}/lesson-notes/${currentNote.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Error deleting note:', error);
      }
    }
    
    setIsRecording(false);
    setIsPaused(false);
    setCurrentNote(null);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setShowSummaryReview(false);
    setRecordingWarning(null);
    setSavedChunks([]);
    setCurrentChunkNumber(0);
    audioChunksRef.current = [];
    allAudioChunksRef.current = [];
    
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
  };

  const removeWorkedOnItem = (index) => {
    setEditedSummary(prev => ({
      ...prev,
      worked_on: prev.worked_on.filter((_, i) => i !== index)
    }));
  };

  const removeHomeworkItem = (index) => {
    setEditedSummary(prev => ({
      ...prev,
      homework: prev.homework.filter((_, i) => i !== index)
    }));
  };

  // Add new item to summary
  const addWorkedOnItem = () => {
    if (newWorkedOnItem.trim()) {
      setEditedSummary(prev => ({
        ...prev,
        worked_on: [...prev.worked_on, newWorkedOnItem.trim()]
      }));
      setNewWorkedOnItem('');
    }
  };

  const addHomeworkItem = () => {
    if (newHomeworkItem.trim()) {
      setEditedSummary(prev => ({
        ...prev,
        homework: [...prev.homework, newHomeworkItem.trim()]
      }));
      setNewHomeworkItem('');
    }
  };

  // Edit existing note functions
  const startEditingNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteData({
      worked_on: [...(note.worked_on || [])],
      homework: [...(note.homework || [])]
    });
    setNewEditWorkedOn('');
    setNewEditHomework('');
  };

  const cancelEditingNote = () => {
    setEditingNoteId(null);
    setEditingNoteData({ worked_on: [], homework: [] });
    setNewEditWorkedOn('');
    setNewEditHomework('');
  };

  const removeEditWorkedOn = (index) => {
    setEditingNoteData(prev => ({
      ...prev,
      worked_on: prev.worked_on.filter((_, i) => i !== index)
    }));
  };

  const removeEditHomework = (index) => {
    setEditingNoteData(prev => ({
      ...prev,
      homework: prev.homework.filter((_, i) => i !== index)
    }));
  };

  const addEditWorkedOn = () => {
    if (newEditWorkedOn.trim()) {
      setEditingNoteData(prev => ({
        ...prev,
        worked_on: [...prev.worked_on, newEditWorkedOn.trim()]
      }));
      setNewEditWorkedOn('');
    }
  };

  const addEditHomework = () => {
    if (newEditHomework.trim()) {
      setEditingNoteData(prev => ({
        ...prev,
        homework: [...prev.homework, newEditHomework.trim()]
      }));
      setNewEditHomework('');
    }
  };

  const saveEditedNote = async () => {
    if (!editingNoteId) return;
    
    try {
      await axios.put(`${API}/lesson-notes/${editingNoteId}`, {
        worked_on: editingNoteData.worked_on,
        homework: editingNoteData.homework
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success('Note opdateret');
      cancelEditingNote();
      fetchCustomerAndNotes();
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Kunne ikke opdatere');
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Slet denne note?')) return;
    
    try {
      await axios.delete(`${API}/lesson-notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note slettet');
      fetchCustomerAndNotes();
    } catch (error) {
      toast.error('Kunne ikke slette');
    }
  };

  // Toggle note expansion
  const toggleNoteExpanded = (noteId) => {
    setExpandedNotes(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f3f0', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Indlæser...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f3f0', padding: '1rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
          <button
            onClick={() => navigate(horseId ? `/admin/noter/${customerId}` : '/admin/noter')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', display: 'flex', alignItems: 'center' }}
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#333' }}>
              🐴 {horse ? horse.name : (customer ? `${customer.first_name} ${customer.last_name}` : 'Kunde')}
            </h1>
            <div style={{ color: '#666', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {horse ? (
                <span>
                  Noter for {customer ? `${customer.first_name} ${customer.last_name}` : 'kunde'}
                </span>
              ) : (
                <span>Undervisningsnoter</span>
              )}
            </div>
          </div>
        </div>

        {/* Recording Warning */}
        {recordingWarning && (
          <div style={{ 
            background: '#fff3cd', 
            border: '1px solid #ffc107', 
            borderRadius: '8px', 
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: '#856404'
          }}>
            <AlertCircle size={18} />
            {recordingWarning}
          </div>
        )}

        {/* Recording Section */}
        {!showSummaryReview && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#333', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Mic size={20} />
              {isRecording ? (isPaused ? 'Optagelse pauset' : 'Optager undervisning...') : (audioBlob ? 'Optagelse klar' : 'Start ny undervisning')}
            </h2>
            
            {/* Show customer's language (not editable) */}
            {!isRecording && !audioBlob && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: '#f5f5f5',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  color: '#666'
                }}>
                  {selectedLanguage === 'da' && '🇩🇰 Dansk'}
                  {selectedLanguage === 'de' && '🇩🇪 Deutsch'}
                  {selectedLanguage === 'en' && '🇬🇧 English'}
                  {!selectedLanguage && '🇩🇰 Dansk'}
                </div>
              </div>
            )}
            
            {/* Recording timer */}
            {(isRecording || audioBlob) && (
              <div style={{ 
                fontSize: '3rem', 
                fontWeight: 600, 
                textAlign: 'center', 
                margin: '1rem 0',
                color: isRecording ? (isPaused ? '#ff9800' : '#c62828') : '#333',
                fontFamily: 'monospace'
              }}>
                {formatTime(recordingTime)}
                {isRecording && (
                  <span style={{ fontSize: '1rem', display: 'block', color: isPaused ? '#ff9800' : '#c62828', animation: isPaused ? 'none' : 'pulse 1s infinite' }}>
                    {isPaused ? '⏸ PAUSET' : '● OPTAGER'}
                  </span>
                )}
              </div>
            )}
            
            {/* Chunk status indicators */}
            {savedChunks.length > 0 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '0.5rem', 
                marginBottom: '1rem',
                flexWrap: 'wrap'
              }}>
                {savedChunks.map((chunk) => (
                  <div 
                    key={chunk.chunkNumber}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      background: chunk.status === 'saved' ? '#e8f5e9' : 
                                 chunk.status === 'error' ? '#ffebee' : '#fff3e0',
                      color: chunk.status === 'saved' ? '#2e7d32' : 
                             chunk.status === 'error' ? '#c62828' : '#f57c00',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}
                  >
                    {chunk.status === 'saving' && <Loader2 size={12} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />}
                    {chunk.status === 'saved' && <CheckCircle2 size={12} />}
                    {chunk.status === 'error' && <X size={12} />}
                    Chunk {chunk.chunkNumber + 1}
                  </div>
                ))}
              </div>
            )}
            
            {/* Recording tip with chunk info */}
            {isRecording && !isPaused && recordingTime > 0 && (
              <div style={{ 
                textAlign: 'center', 
                color: '#666', 
                fontSize: '0.75rem', 
                marginBottom: '0.5rem',
                opacity: 0.8
              }}>
                Næste auto-gem om: {formatTime(600 - (recordingTime % 600))}
              </div>
            )}
            
            {/* Audio playback */}
            {audioUrl && !isRecording && (
              <audio ref={audioRef} src={audioUrl} onEnded={() => setIsPlaying(false)} style={{ display: 'none' }} />
            )}
            
            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {!isRecording && !audioBlob && (
                <button
                  onClick={startRecording}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '1rem 2rem',
                    background: '#c62828',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '1rem'
                  }}
                >
                  <Mic size={24} />
                  Start optagelse
                </button>
              )}
              
              {isRecording && (
                <>
                  {isPaused ? (
                    <button
                      onClick={resumeRecording}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '1rem 2rem',
                        background: '#4caf50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '1rem'
                      }}
                    >
                      <Play size={24} />
                      Genoptag
                    </button>
                  ) : (
                    <button
                      onClick={pauseRecording}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '1rem 2rem',
                        background: '#ff9800',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '1rem'
                      }}
                    >
                      <Pause size={24} />
                      Pause
                    </button>
                  )}
                  <button
                    onClick={stopRecording}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '1rem 2rem',
                      background: '#333',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  >
                    <Square size={24} />
                    Stop
                  </button>
                </>
              )}
              
              {audioBlob && !isRecording && (
                <>
                  <button
                    onClick={togglePlayback}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      background: '#f5f5f5',
                      color: '#333',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                    {isPlaying ? 'Pause' : 'Afspil'}
                  </button>
                  
                  <button
                    onClick={transcribeAndSummarize}
                    disabled={isTranscribing || isSummarizing}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      background: isTranscribing || isSummarizing ? '#ccc' : '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: isTranscribing || isSummarizing ? 'not-allowed' : 'pointer',
                      fontWeight: 500
                    }}
                  >
                    <FileText size={20} />
                    {isTranscribing ? 'Transskriberer...' : (isSummarizing ? 'Laver sammendrag...' : 'Lav sammendrag')}
                  </button>
                  
                  <button
                    onClick={cancelRecording}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      background: '#ffebee',
                      color: '#c62828',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    <X size={20} />
                    Slet
                  </button>
                </>
              )}
            </div>
            
            {/* Recording tip */}
            {isRecording && !isPaused && (
              <p style={{ textAlign: 'center', color: '#666', fontSize: '0.85rem', marginTop: '1rem' }}>
                💡 Optagelsen fortsætter selvom du skifter app eller modtager opkald
              </p>
            )}
          </div>
        )}

        {/* Summary Review Section */}
        {showSummaryReview && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#333' }}>
              ✨ Gennemgå sammendrag
            </h2>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
              Fjern punkter med X, eller tilføj dine egne med +
            </p>
            
            {/* Worked on */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem', color: '#7c6f5e' }}>
                📝 Vi arbejdede med:
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {editedSummary.worked_on.map((item, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    background: '#f9f9f9',
                    padding: '0.75rem',
                    borderRadius: '8px'
                  }}>
                    <span style={{ flex: 1 }}>{item}</span>
                    <button
                      onClick={() => removeWorkedOnItem(index)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: '0.25rem' }}
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
                {/* Add new worked on item */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={newWorkedOnItem}
                    onChange={(e) => setNewWorkedOnItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addWorkedOnItem()}
                    placeholder="Tilføj nyt punkt..."
                    style={{
                      flex: 1,
                      padding: '0.5rem 0.75rem',
                      border: '1px dashed #ccc',
                      borderRadius: '8px',
                      fontSize: '0.9rem'
                    }}
                  />
                  <button
                    onClick={addWorkedOnItem}
                    disabled={!newWorkedOnItem.trim()}
                    style={{
                      padding: '0.5rem 0.75rem',
                      background: newWorkedOnItem.trim() ? '#7c6f5e' : '#e0e0e0',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: newWorkedOnItem.trim() ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Homework */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem', color: '#5a8b9e' }}>
                🎯 Hjemmearbejde:
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {editedSummary.homework.map((item, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    background: '#f0f7fa',
                    padding: '0.75rem',
                    borderRadius: '8px'
                  }}>
                    <span style={{ flex: 1 }}>{item}</span>
                    <button
                      onClick={() => removeHomeworkItem(index)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: '0.25rem' }}
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
                {/* Add new homework item */}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={newHomeworkItem}
                    onChange={(e) => setNewHomeworkItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addHomeworkItem()}
                    placeholder="Tilføj nyt hjemmearbejde..."
                    style={{
                      flex: 1,
                      padding: '0.5rem 0.75rem',
                      border: '1px dashed #ccc',
                      borderRadius: '8px',
                      fontSize: '0.9rem'
                    }}
                  />
                  <button
                    onClick={addHomeworkItem}
                    disabled={!newHomeworkItem.trim()}
                    style={{
                      padding: '0.5rem 0.75rem',
                      background: newHomeworkItem.trim() ? '#5a8b9e' : '#e0e0e0',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: newHomeworkItem.trim() ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={cancelRecording}
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '0.75rem',
                  background: '#f5f5f5',
                  color: '#666',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Annuller
              </button>
              <button
                onClick={() => saveSummary(false)}
                style={{
                  flex: 1,
                  minWidth: '120px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  background: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                <Check size={20} />
                Gem
              </button>
              <button
                onClick={() => saveSummary(true)}
                style={{
                  flex: 1,
                  minWidth: '150px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  background: '#25D366',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                <MessageCircle size={20} />
                Gem & Kopier
              </button>
            </div>
          </div>
        )}

        {/* Previous Notes */}
        <div>
          <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#333' }}>
            📚 Tidligere undervisning
          </h2>
          
          {notes.length === 0 ? (
            <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', textAlign: 'center', color: '#666' }}>
              Ingen tidligere noter for denne kunde
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {notes.map(note => {
                const isExpanded = expandedNotes[note.id] || editingNoteId === note.id;
                const pointsCount = (note.worked_on?.length || 0) + (note.homework?.length || 0);
                
                return (
                <div key={note.id} style={{ 
                  background: 'white', 
                  borderRadius: '12px', 
                  overflow: 'hidden',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
                }}>
                  {/* Collapsible header - always visible */}
                  <div 
                    onClick={() => toggleNoteExpanded(note.id)}
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      padding: '0.75rem 1rem',
                      cursor: 'pointer',
                      background: isExpanded ? '#fafafa' : 'white'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <Clock size={14} />
                      {new Date(note.lesson_date).toLocaleDateString('da-DK', { 
                        weekday: 'short',
                        day: 'numeric', 
                        month: 'short'
                      })}
                      <span style={{ 
                        padding: '0.15rem 0.4rem', 
                        background: '#f0f0f0', 
                        borderRadius: '4px',
                        fontSize: '0.7rem'
                      }}>
                        {note.language === 'da' ? '🇩🇰' : note.language === 'de' ? '🇩🇪' : '🇬🇧'}
                      </span>
                      {!isExpanded && pointsCount > 0 && (
                        <span style={{ color: '#999', fontSize: '0.8rem' }}>
                          ({pointsCount} punkter)
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                      {/* Edit button */}
                      <button
                        onClick={(e) => { e.stopPropagation(); startEditingNote(note); }}
                        title="Rediger note"
                        style={{ background: '#fff3e0', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#f57c00', padding: '0.35rem', display: 'flex', alignItems: 'center' }}
                      >
                        <Pencil size={14} />
                      </button>
                      {/* Copy to WhatsApp button */}
                      <button
                        onClick={(e) => { e.stopPropagation(); copyNoteToClipboard(note); }}
                        title="Kopier til WhatsApp"
                        style={{ background: '#e8f5e9', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#25D366', padding: '0.35rem', display: 'flex', alignItems: 'center' }}
                      >
                        <MessageCircle size={14} />
                      </button>
                      {/* Release to portal button */}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleReleaseToPortal(note); }}
                        title={note.released_to_portal ? 'Skjul fra kundens profil' : 'Frigiv til kundens profil'}
                        style={{ 
                          background: note.released_to_portal ? '#e3f2fd' : '#f5f5f5', 
                          border: 'none', 
                          borderRadius: '4px', 
                          cursor: 'pointer', 
                          color: note.released_to_portal ? '#1976d2' : '#999', 
                          padding: '0.35rem', 
                          display: 'flex', 
                          alignItems: 'center' 
                        }}
                      >
                        {note.released_to_portal ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }}
                        title="Slet note"
                        style={{ background: '#ffebee', border: 'none', borderRadius: '4px', cursor: 'pointer', color: '#c62828', padding: '0.35rem', display: 'flex', alignItems: 'center' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Expandable content */}
                  {isExpanded && (
                    <div style={{ padding: '0 1rem 1rem 1rem', borderTop: '1px solid #f0f0f0' }}>
                  {/* Edit mode for existing note */}
                  {editingNoteId === note.id ? (
                    <div style={{ paddingTop: '0.75rem' }}>
                      {/* Edit worked_on */}
                      <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#7c6f5e' }}>📝 Vi arbejdede med:</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {editingNoteData.worked_on.map((item, index) => (
                            <div key={index} style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              background: '#f9f9f9',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '6px'
                            }}>
                              <span style={{ flex: 1, fontSize: '0.9rem' }}>{item}</span>
                              <button
                                onClick={() => removeEditWorkedOn(index)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: '0.25rem' }}
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                          {/* Add new worked_on item */}
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                              type="text"
                              value={newEditWorkedOn}
                              onChange={(e) => setNewEditWorkedOn(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && addEditWorkedOn()}
                              placeholder="Tilføj nyt punkt..."
                              style={{
                                flex: 1,
                                padding: '0.5rem 0.75rem',
                                border: '1px dashed #ccc',
                                borderRadius: '6px',
                                fontSize: '0.9rem'
                              }}
                            />
                            <button
                              onClick={addEditWorkedOn}
                              disabled={!newEditWorkedOn.trim()}
                              style={{
                                padding: '0.5rem 0.75rem',
                                background: newEditWorkedOn.trim() ? '#7c6f5e' : '#e0e0e0',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: newEditWorkedOn.trim() ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Edit homework */}
                      <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#5a8b9e' }}>🎯 Hjemmearbejde:</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {editingNoteData.homework.map((item, index) => (
                            <div key={index} style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              background: '#f0f7fa',
                              padding: '0.5rem 0.75rem',
                              borderRadius: '6px'
                            }}>
                              <span style={{ flex: 1, fontSize: '0.9rem' }}>{item}</span>
                              <button
                                onClick={() => removeEditHomework(index)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: '0.25rem' }}
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                          {/* Add new homework item */}
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <input
                              type="text"
                              value={newEditHomework}
                              onChange={(e) => setNewEditHomework(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && addEditHomework()}
                              placeholder="Tilføj nyt hjemmearbejde..."
                              style={{
                                flex: 1,
                                padding: '0.5rem 0.75rem',
                                border: '1px dashed #ccc',
                                borderRadius: '6px',
                                fontSize: '0.9rem'
                              }}
                            />
                            <button
                              onClick={addEditHomework}
                              disabled={!newEditHomework.trim()}
                              style={{
                                padding: '0.5rem 0.75rem',
                                background: newEditHomework.trim() ? '#5a8b9e' : '#e0e0e0',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: newEditHomework.trim() ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Plus size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Save/Cancel buttons */}
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={cancelEditingNote}
                          style={{
                            flex: 1,
                            padding: '0.5rem',
                            background: '#f5f5f5',
                            color: '#666',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}
                        >
                          Annuller
                        </button>
                        <button
                          onClick={saveEditedNote}
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            padding: '0.5rem',
                            background: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 500
                          }}
                        >
                          <Check size={16} />
                          Gem ændringer
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ paddingTop: '0.75rem' }}>
                      {note.worked_on && note.worked_on.length > 0 && (
                        <div style={{ marginBottom: '0.75rem' }}>
                          <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.85rem', color: '#7c6f5e' }}>Vi arbejdede med:</h4>
                          <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#333', fontSize: '0.9rem' }}>
                            {note.worked_on.map((item, i) => (
                              <li key={i} style={{ marginBottom: '0.25rem' }}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {note.homework && note.homework.length > 0 && (
                        <div>
                          <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.85rem', color: '#5a8b9e' }}>Hjemmearbejde:</h4>
                          <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#333', fontSize: '0.9rem' }}>
                            {note.homework.map((item, i) => (
                              <li key={i} style={{ marginBottom: '0.25rem' }}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {note.sent_to_customer && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#4caf50', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <Check size={12} />
                          Sendt til kunde
                        </div>
                      )}
                    </div>
                  )}
                    </div>
                  )}
                </div>
              )})}
            </div>
          )}
        </div>
      </div>
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CustomerLessonNotesPage;
